import { useEffect, useMemo, useRef, useState } from "react";

/**
 * MemberPicker — shared chip-input for selecting tenant members.
 *
 * Used by:
 *   - Contact admin proxy field (mode=single, kinds=[user])
 *   - User escalates-to field (mode=single, kinds=[user, contact])
 *   - Action Item RASCI pickers (mode=multi, varies)
 *   - Escalation policy explicit-target tier (mode=multi, all kinds)
 *
 * Does NOT fetch — callers pass `candidates`. Keeps the component composable.
 *
 * Spec: governance-foundation §19.2.1.
 *
 * **Do not wrap this component in a native <label>.** A label binds all
 * clicks inside it to its associated form control, which in this component
 * is the hidden search input — causing focus/dropdown bleed when the user
 * clicks the visible header text. Callers should use a <div className="field">
 * with a <span className="label"> header instead.
 */

export type MemberKind = "user" | "contact" | "group";

export interface MemberCandidate {
  id: string;
  kind: MemberKind;
  display_name: string;
  email?: string;
}

export interface MemberPickerProps {
  mode: "single" | "multi";
  allowedKinds: MemberKind[];
  candidates: MemberCandidate[];
  selected: string[];
  onChange: (next: string[]) => void;
  excludeIds?: string[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
}

const KIND_LABEL: Record<MemberKind, string> = { user: "U", contact: "C", group: "G" };

export function MemberPicker({
  mode,
  allowedKinds,
  candidates,
  selected,
  onChange,
  excludeIds = [],
  placeholder,
  required,
  disabled,
  errorMessage
}: MemberPickerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const candidateById = useMemo(() => {
    const m = new Map<string, MemberCandidate>();
    for (const c of candidates) m.set(c.id, c);
    return m;
  }, [candidates]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const excludeSet = new Set(excludeIds);
    const selectedSet = new Set(selected);
    return candidates.filter((c) => {
      if (!allowedKinds.includes(c.kind)) return false;
      if (excludeSet.has(c.id)) return false;
      if (mode === "multi" && selectedSet.has(c.id)) return false;
      if (!q) return true;
      const haystack = `${c.display_name} ${c.email ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [candidates, allowedKinds, excludeIds, selected, query, mode]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, filtered.length]);

  // Close when clicking outside — pointerdown to beat the option's mousedown.
  useEffect(() => {
    if (!open) return;
    function handler(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  function commit(candidate: MemberCandidate) {
    if (mode === "single") {
      onChange([candidate.id]);
      setQuery("");
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (selected.includes(candidate.id)) {
      onChange(selected.filter((id) => id !== candidate.id));
    } else {
      onChange([...selected, candidate.id]);
    }
    setQuery("");
    // Keep dropdown open in multi-mode so the user can add more.
    setOpen(true);
    inputRef.current?.focus();
  }

  function removeChip(id: string) {
    onChange(selected.filter((x) => x !== id));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && filtered[activeIdx]) {
        e.preventDefault();
        commit(filtered[activeIdx]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Backspace" && query === "" && selected.length > 0) {
      e.preventDefault();
      onChange(selected.slice(0, -1));
    }
  }

  const chips = selected
    .map((id) => candidateById.get(id))
    .filter((c): c is MemberCandidate => Boolean(c));

  function kindClass(k: MemberKind): string {
    return `memberPickerKind memberPickerKind-${k}`;
  }

  return (
    <div className="memberPickerWrap" ref={wrapRef}>
      <div
        className="memberPickerChips"
        onClick={(e) => {
          // Only refocus if the click landed on the chip container itself,
          // not a chip or its × button.
          if (e.target === e.currentTarget && !disabled) {
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        {chips.map((c) => (
          <span key={c.id} className={`pill memberPickerChip memberPickerChip-${c.kind}`} title={c.email ?? ""}>
            <span className={kindClass(c.kind)} aria-hidden="true">{KIND_LABEL[c.kind]}</span>
            <span className="memberPickerChipLabel">{c.display_name}</span>
            {!disabled && (
              <button
                type="button"
                className="memberPickerChipRemove"
                aria-label={`Remove ${c.display_name}`}
                onPointerDown={(e) => {
                  // Prevent the pointerdown-outside-closes-dropdown handler
                  // from firing + prevent the chip's parent row click.
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(c.id);
                }}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          className="memberPickerInput"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={chips.length === 0 ? placeholder : ""}
          disabled={disabled}
          required={required && chips.length === 0}
        />
      </div>
      {open && !disabled && filtered.length > 0 && (
        <div className="memberPickerDropdown" role="listbox">
          {filtered.map((c, idx) => (
            <div
              key={c.id}
              role="option"
              aria-selected={idx === activeIdx}
              className={`memberPickerOption${idx === activeIdx ? " isActive" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(c);
              }}
              onMouseEnter={() => setActiveIdx(idx)}
            >
              <span className={kindClass(c.kind)} aria-hidden="true">{KIND_LABEL[c.kind]}</span>
              <span style={{ flex: 1 }}>{c.display_name}</span>
              {c.email && <span className="muted" style={{ fontSize: 12 }}>{c.email}</span>}
            </div>
          ))}
        </div>
      )}
      {open && !disabled && filtered.length === 0 && (
        <div className="memberPickerDropdown memberPickerDropdownEmpty">
          <div className="muted" style={{ padding: "6px 8px", fontSize: 12 }}>
            {query ? "No matches." : "No candidates available."}
          </div>
        </div>
      )}
      {errorMessage && <div className="help" style={{ color: "var(--danger-text)" }}>{errorMessage}</div>}
    </div>
  );
}
